import { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { getDepartments } from '../../services/firebase/departmentService';
import { useAuth } from '../../context/AuthContext';
import { isDeptVisibleToUser } from '../../utils/departmentVisibility';

const DepartmentSelector = ({
    value,
    onChange,
    onSelect,
    includeAll = false,
    activeOnly = true,
    disabled = false,
    required = false,
    className = ''
}) => {
    const { user } = useAuth();
    const [departments, setDepartments] = useState([]);
    const [flatList, setFlatList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastFetch, setLastFetch] = useState(null);

    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    const flattenDepartments = (depts, level = 0) => {
        return depts.flatMap(dept => [
            { 
                ...dept, 
                displayName: "\u00A0\u00A0\u00A0\u00A0".repeat(level) + (level > 0 ? "└── " : "") + dept.name 
            },
            ...(dept.children ? flattenDepartments(dept.children, level + 1) : [])
        ]);
    };

    const fetchDepartments = async (force = false) => {
        // Check cache
        if (!force && lastFetch && (Date.now() - lastFetch < CACHE_TTL)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const filters = {
                hierarchical: true,
                ...(activeOnly ? { status: 'active' } : {})
            };
            const response = await getDepartments(filters);

            // Extract data array from response
            const data = response?.data || response || [];

            // Flatten the hierarchy for the dropdown
            let flattened = Array.isArray(data) ? flattenDepartments(data) : [];

            // Filter by department visibility if user role is restricted
            const isDeptOrProdHead = user?.role === 'dept_head' || user?.role === 'production_head';
            if (isDeptOrProdHead) {
                flattened = flattened.filter(dept =>
                    isDeptVisibleToUser(dept.code, dept.name, user)
                );
            }

            setDepartments(data); // Store raw tree if needed
            setFlatList(flattened);
            setLastFetch(Date.now());
        } catch (err) {
            console.error('Error fetching departments:', err);
            setError('Failed to load departments');
            setDepartments([]);
            setFlatList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDepartments();
    }, [activeOnly]);

    // Auto-refresh when departments might have changed
    useEffect(() => {
        const interval = setInterval(() => {
            fetchDepartments();
        }, CACHE_TTL);

        return () => clearInterval(interval);
    }, [activeOnly]);

    const handleChange = (e) => {
        const selectedCode = e.target.value;
        if (onChange) {
            onChange(selectedCode);
        }
        
        if (onSelect) {
            const dept = flatList.find(d => d.code === selectedCode);
            onSelect(dept || null);
        }
    };

    if (error) {
        return (
            <Form.Select disabled className={className}>
                <option>Error loading departments</option>
            </Form.Select>
        );
    }

    return (
        <Form.Select
            value={value || ''}
            onChange={handleChange}
            disabled={disabled || loading}
            required={required}
            className={className}
        >
            {includeAll && <option value="">All Departments</option>}
            {!includeAll && !required && <option value="">Select Department</option>}

            {loading ? (
                <option disabled>Loading...</option>
            ) : (
                flatList.map((dept, index) => (
                    <option key={dept.code || index} value={dept.code}>
                        {dept.displayName}
                    </option>
                ))
            )}
        </Form.Select>
    );
};

export default DepartmentSelector;
